import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Plus,
  Wrench,
  CheckCircle,
  AlertTriangle,
  Calendar,
  User,
  Play,
  Loader2,
  ChevronDown,
  Clock,
  FileText, // Placeholder icon for maintenance type
  ChevronRight,
} from "lucide-react";
import { maintenanceAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { PERMISSIONS } from "../constants/roles";
import HasPermission from "../components/HasPermission";

const statusConfig = {
  scheduled: {
    label: "Scheduled",
    color: "badge-primary",
    icon: Calendar,
    colorClass: "text-blue-500",
  },
  in_progress: {
    label: "In Progress",
    color: "badge-warning",
    icon: Play,
    colorClass: "text-yellow-500",
  },
  completed: {
    label: "Completed",
    color: "badge-success",
    icon: CheckCircle,
    colorClass: "text-green-500",
  },
  cancelled: {
    label: "Cancelled",
    color: "badge-neutral",
    icon: AlertTriangle,
    colorClass: "text-slate-500",
  },
  overdue: {
    label: "Overdue",
    color: "badge-danger",
    icon: AlertTriangle,
    colorClass: "text-red-500",
  },
};

const maintenanceTypes = {
  preventive: { label: "Preventive", icon: Clock, colorClass: "text-cyan-500" },
  corrective: {
    label: "Corrective",
    icon: AlertTriangle,
    colorClass: "text-orange-500",
  },
  predictive: {
    label: "Predictive",
    icon: Search,
    colorClass: "text-purple-500",
  },
};

const SummaryCard = ({ title, value, icon: Icon, colorKey }) => {
  const colorClasses = {
    blue: {
      bg: "from-blue-50 to-white border-blue-100",
      icon: "bg-blue-100 text-blue-600",
    },
    yellow: {
      bg: "from-amber-50 to-white border-amber-100",
      icon: "bg-amber-100 text-amber-600",
    },
    red: {
      bg: "from-rose-50 to-white border-rose-100",
      icon: "bg-rose-100 text-rose-600",
    },
  };

  const colors = colorClasses[colorKey] || colorClasses.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx(
        "p-6 bg-gradient-to-br border rounded-2xl shadow-sm flex items-center gap-4 transition-shadow",
        colors.bg,
      )}
    >
      <div
        className={clsx(
          "w-12 h-12 rounded-xl flex items-center justify-center shadow-sm",
          colors.icon,
        )}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 mb-1">{value}</p>
        <p className="text-sm font-medium text-slate-600">{title}</p>
      </div>
    </motion.div>
  );
};

const MaintenanceListItem = ({ maintenance, index, onStart, isStarting }) => {
  const status = maintenance.status
    ? statusConfig[maintenance.status]
    : statusConfig.scheduled;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link
        to={`/maintenance/${maintenance.id}`}
        className="grid grid-cols-12 items-center p-4 text-sm border-b border-slate-100 last:border-b-0 transition-colors hover:bg-slate-50 group"
      >
        {/* Column 1: Title & Machine (span 4) */}
        <div className="col-span-4 flex flex-col min-w-0 pr-4">
          <span className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors truncate">
            {maintenance.title}
          </span>
          <span className="text-xs text-slate-500 truncate">
            {maintenance.machine_name}
          </span>
        </div>

        {/* Column 2: Status (span 2) */}
        <div className="col-span-2 flex items-center gap-2 text-slate-700">
          {StatusIcon && (
            <StatusIcon className={clsx("w-4 h-4", status.colorClass)} />
          )}
          <span>{status.label}</span>
        </div>

        {/* Column 3: Assigned To (span 2) */}
        <div className="col-span-2 text-slate-700 truncate">
          {maintenance.technician_name || "Unassigned"}
        </div>

        {/* Column 4: Scheduled Date (span 2) */}
        <div className="col-span-2 text-slate-600">
          {maintenance.scheduled_date
            ? new Date(maintenance.scheduled_date).toLocaleDateString()
            : "N/A"}
        </div>

        {/* Column 5: Actions (span 2) */}
        <div className="col-span-2 flex items-center justify-end gap-2">
          {(maintenance.status === "scheduled" ||
            maintenance.status === "overdue") && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onStart(maintenance.id);
              }}
              disabled={isStarting}
              className="btn btn-primary btn-sm shadow-sm gap-1.5"
            >
              {isStarting ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Play className="w-3 h-3" />
              )}
              Start
            </button>
          )}
          {maintenance.status === "in_progress" && (
            <Link
              to={`/maintenance/${maintenance.id}/execute`}
              className="btn btn-warning btn-sm shadow-sm"
              onClick={(e) => e.stopPropagation()}
            >
              Continue
            </Link>
          )}
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-transform group-hover:translate-x-0.5 ml-1" />
        </div>
      </Link>
    </motion.div>
  );
};

const Maintenance = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const statusRef = useRef(null);
  const typeRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setIsStatusOpen(false);
      }
      if (typeRef.current && !typeRef.current.contains(event.target)) {
        setIsTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: maintenanceData, isLoading } = useQuery({
    queryKey: [
      "maintenance",
      { search, status: statusFilter, type: typeFilter },
    ],
    queryFn: () =>
      maintenanceAPI
        .getAll({
          search: search || undefined,
          status: statusFilter || undefined,
          type: typeFilter || undefined,
        })
        .then((res) => res.data),
  });

  const startMutation = useMutation({
    mutationFn: (id) => maintenanceAPI.start(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance"] });
      toast.success("Ticket claimed and started.");
      navigate(`/maintenance/${id}/execute`);
    },
    onError: (err) =>
      toast.error("Failed to start maintenance: " + err.message),
  });

  const { data: overdueData } = useQuery({
    queryKey: ["maintenance-overdue"],
    queryFn: () => maintenanceAPI.getOverdue().then((res) => res.data.data),
  });

  const maintenance = maintenanceData?.data || [];

  const totalTasks = maintenanceData?.pagination?.total || maintenance.length;
  const inProgressCount = maintenance.filter(
    (m) => m.status === "in_progress",
  ).length;
  const overdueCount = overdueData?.length || 0;

  // Helper to get type icon/label for filter button display
  const TypeIconToDisplay = typeFilter
    ? maintenanceTypes[typeFilter]?.icon
    : null;
  const typeColorToDisplay = typeFilter
    ? maintenanceTypes[typeFilter]?.colorClass
    : null;

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Maintenance</h1>
          <p className="text-slate-500">
            Schedule and track maintenance activities
          </p>
        </div>
        <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
          <Link
            to="/maintenance/schedule"
            className="btn btn-primary shadow-md"
          >
            <Plus className="w-4 h-4" />
            Schedule Maintenance
          </Link>
        </HasPermission>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          title="Total Tasks"
          value={totalTasks}
          icon={Wrench}
          colorKey="blue"
        />
        <SummaryCard
          title="In Progress"
          value={inProgressCount}
          icon={Play}
          colorKey="yellow"
        />
        <SummaryCard
          title="Overdue"
          value={overdueCount}
          icon={AlertTriangle}
          colorKey="red"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search maintenance..."
            className="input w-full h-10 pl-12 bg-white border-slate-200"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Status Filter Dropdown */}
          <div ref={statusRef} className="relative">
            <button
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="btn btn-secondary h-10 w-full sm:w-auto justify-between"
            >
              <span className="flex items-center gap-2">
                {statusFilter && statusConfig[statusFilter] ? (
                  <>
                    {React.createElement(statusConfig[statusFilter].icon, {
                      className: clsx(
                        "w-4 h-4",
                        statusConfig[statusFilter].colorClass,
                      ),
                    })}
                    {statusConfig[statusFilter].label}
                  </>
                ) : (
                  "All Status"
                )}
              </span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isStatusOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isStatusOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setStatusFilter("");
                        setIsStatusOpen(false);
                      }}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                        statusFilter === ""
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      All Status
                    </button>
                    {Object.entries(statusConfig).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            setStatusFilter(key);
                            setIsStatusOpen(false);
                          }}
                          className={clsx(
                            "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                            statusFilter === key
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700 hover:bg-slate-50",
                          )}
                        >
                          <Icon
                            className={clsx("w-4 h-4 mr-3", config.colorClass)}
                          />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Type Filter Dropdown */}
          <div ref={typeRef} className="relative">
            <button
              onClick={() => setIsTypeOpen(!isTypeOpen)}
              className="btn btn-secondary h-10 w-full sm:w-auto justify-between"
            >
              <span className="flex items-center gap-2">
                {TypeIconToDisplay && (
                  <TypeIconToDisplay
                    className={clsx("w-4 h-4", typeColorToDisplay)}
                  />
                )}
                {typeFilter ? maintenanceTypes[typeFilter].label : "All Types"}
              </span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isTypeOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isTypeOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setTypeFilter("");
                        setIsTypeOpen(false);
                      }}
                      className={clsx(
                        "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                        typeFilter === ""
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-slate-50",
                      )}
                    >
                      All Types
                    </button>
                    {Object.entries(maintenanceTypes).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            setTypeFilter(key);
                            setIsTypeOpen(false);
                          }}
                          className={clsx(
                            "flex items-center w-full px-4 py-2 text-sm text-left transition-colors",
                            typeFilter === key
                              ? "bg-blue-50 text-blue-700"
                              : "text-slate-700 hover:bg-slate-50",
                          )}
                        >
                          <Icon
                            className={clsx("w-4 h-4 mr-3", config.colorClass)}
                          />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card overflow-hidden p-0">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-12 items-center p-4 border-b border-slate-200 last:border-b-0"
            >
              <div className="col-span-4 pr-4">
                <div className="h-5 w-48 skeleton mb-1" />
                <div className="h-3 w-24 skeleton" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-20 skeleton" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-28 skeleton" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-24 skeleton" />
              </div>
              <div className="col-span-2 flex justify-end">
                <div className="h-7 w-20 skeleton rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : maintenance.length > 0 ? (
        <div className="card overflow-hidden p-0">
          <div className="grid grid-cols-12 items-center text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-b border-slate-200 px-4 py-3">
            <div className="col-span-4">Task / Machine</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Assigned To</div>
            <div className="col-span-2">Scheduled</div>
            <div className="col-span-2"></div> {/* Action column */}
          </div>
          <div className="divide-y divide-slate-100">
            <AnimatePresence>
              {maintenance.map((item, index) => (
                <MaintenanceListItem
                  key={item.id}
                  maintenance={item}
                  index={index}
                  onStart={(id) => startMutation.mutate(id)}
                  isStarting={
                    startMutation.isPending &&
                    startMutation.variables === item.id
                  }
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <Wrench className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-800 mb-2">
            No maintenance records
          </h3>
          <p className="text-slate-500 mb-6 max-w-sm mx-auto">
            {statusFilter || typeFilter
              ? "Try adjusting your filters to find what you're looking for."
              : "Schedule your first maintenance activity to start tracking machine health."}
          </p>
          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
            <Link
              to="/maintenance/schedule"
              className="btn btn-primary shadow-md inline-flex"
            >
              <Plus className="w-4 h-4" />
              Schedule Maintenance
            </Link>
          </HasPermission>
        </div>
      )}
    </div>
  );
};

export default Maintenance;
